import { useMemo, useState } from "react";
import { comics, comicCategories } from "@/mocks/comics";

interface UpdatesProps {
  active: string;
  onSelect: (key: string) => void;
  searchTerm: string;
}

export default function Updates({ active, onSelect, searchTerm }: UpdatesProps) {
  const [expanded, setExpanded] = useState<number | null>(null);

  const filtered = useMemo(() => {
    const kw = searchTerm.trim();
    return comics.filter((c) => {
      const matchCat = active === "全部" || c.category === active;
      const matchKw =
        !kw ||
        c.title.includes(kw) ||
        c.tags.some((t) => t.includes(kw)) ||
        c.category.includes(kw);
      return matchCat && matchKw;
    });
  }, [active, searchTerm]);

  const toggle = (id: number) => setExpanded((v) => (v === id ? null : id));

  return (
    <section id="updates" className="w-full bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mb-8 flex flex-col items-start justify-between gap-5 md:flex-row md:items-end">
          <div>
            <span className="text-sm font-bold tracking-wide text-accent-700">DAILY UPDATES</span>
            <h2 className="mt-2 text-2xl font-black text-foreground-950 md:text-4xl">今日更新 · 反转剧情在线追更</h2>
            <p className="mt-3 max-w-xl text-sm text-foreground-600 md:text-base">
              每天都有新的恶搞反转与脑洞神展开上线，点进「开始追更」即可跟读最新一话。
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {["全部", ...comicCategories.slice(1).map((c) => c.key)].map((k) => (
              <button
                key={k}
                onClick={() => onSelect(k)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                  active === k
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {k}
              </button>
            ))}
          </div>
        </div>

        {searchTerm && (
          <p className="mb-6 text-sm text-foreground-600">
            关键词 <strong className="text-primary-600">「{searchTerm}」</strong> 的筛选结果共{" "}
            <strong>{filtered.length}</strong> 部
          </p>
        )}

        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <i className="ri-inbox-2-line text-4xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-600">没有找到相关漫画，换个关键词试试吧～</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {filtered.map((comic) => (
              <article
                key={comic.id}
                className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-all hover:-translate-y-1"
              >
                <div className="relative aspect-[5/7] w-full overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - ${comic.category}漫画封面`}
                    title={`${comic.title} 韩国歪歪漫画`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className={`absolute left-2 top-2 rounded-full px-2.5 py-1 text-xs font-bold ${comic.tagColor}`}>
                    {comic.category}
                  </span>
                  <span className="absolute bottom-2 right-2 rounded-full bg-black/55 px-2 py-0.5 text-xs text-background-50">
                    <i className="ri-eye-line"></i> {comic.followers}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-3 md:p-4">
                  <h3 className="text-sm font-bold leading-snug text-foreground-950 md:text-base">
                    {comic.title}
                  </h3>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {comic.tags.map((t) => (
                      <span key={t} className="rounded bg-secondary-100 px-1.5 py-0.5 text-[11px] text-secondary-800">
                        {t}
                      </span>
                    ))}
                  </div>

                  <p
                    className={`mt-2 text-xs leading-relaxed text-foreground-600 ${
                      expanded === comic.id ? "" : "line-clamp-2"
                    }`}
                  >
                    {comic.summary}
                  </p>
                  {expanded === comic.id && (
                    <button onClick={() => toggle(comic.id)} className="mt-1 self-start text-xs text-primary-600">
                      收起
                    </button>
                  )}

                  <div className="mt-auto flex items-center justify-between border-t border-background-100 pt-2.5 text-xs text-foreground-500">
                    <span className="whitespace-nowrap">{comic.updateTime}</span>
                    <button
                      onClick={() => toggle(comic.id)}
                      className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-bold text-accent-800 transition-colors hover:bg-accent-200 whitespace-nowrap"
                    >
                      开始追更 <i className="ri-play-fill"></i>
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "韩国歪歪漫画今日更新",
            itemListElement: filtered.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: c.title,
              url: `https://hahasnap.github.io/`,
            })),
          }),
        }}
      />
    </section>
  );
}